import "./MapaFincasPage.css";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { GOOGLE_MAPS_API_KEY } from "../../../constantes";
import { ConfirmDialogComponent } from "../../../shared/components/ConfirmDialogComponent";
import { errorMessageComponent } from "../../../shared/components/error/ErrorMessageComponent";
import { BasePageComponent } from "../../../shared/components/pages/BasePageComponent";
import { DuenoFincaMapa, FincaMapaResponse } from "../../../shared/response/terceros/FincaMapaResponse";
import { FincaDeleteService } from "../../services/terceros/FincaDeleteService";
import { FincaEliminarService } from "../../services/terceros/FincaEliminarService";
import { FincaMapaService } from "../../services/terceros/FincaMapaService";
import { coincideBusqueda, palabrasBusqueda } from "../../../shared/utils/busquedaTexto";
import { FincaAsignarClienteModal } from "./FincaAsignarClienteModal";

/** Zoom desde el que se dibujan los polígonos (AOI) en lugar de los puntos: más lejos, un AOI es un píxel. */
const ZOOM_POLIGONOS = 13;
/** Color de todas las fincas, con o sin cliente (2026-10-16: las sin cliente dejaron de ser grises). */
const COLOR = "#F97316";
/** Resultados que muestra el buscador; si hay más, avisa cuántos faltan para que se siga escribiendo. */
const MAX_RESULTADOS = 10;
/** Zoom al que se lleva una finca sin AOI elegida en el buscador (se ve solo su punto). */
const ZOOM_PUNTO = 16;
/** Zoom máximo al que el buscador acerca una finca con AOI: una finca chica no queda en primerísimo plano. */
const ZOOM_MAX_FINCA = 18;

/** Capas de una finca: el punto siempre; el polígono solo si tiene AOI. */
interface CapasFinca { punto: L.Marker; poligono: L.GeoJSON | null }

/**
 * Mapa global de fincas (menú "Mapa de fincas"): todas las fincas en un mapa satelital. Lejos se ven como
 * puntos; de cerca, su AOI. Las fincas sin cliente se ven igual que las demás, con 0 en su círculo (pedido del
 * usuario, 2026-10-16; hasta entonces eran grises). Al tocar una finca se ven sus clientes,
 * cada uno con el nombre que le da a la finca: se puede quitar la finca de cualquiera de ellos o asignarla
 * a otro cliente. Un promotor ve y toca solo las de su cartera (lo filtra y lo valida el Server).
 * El buscador del encabezado (2026-10-28) ubica una finca por su nombre o el de sus clientes y abre su popup;
 * no esconde las demás.
 */
export class MapaFincasPage extends BasePageComponent {
  private _map: L.Map | null = null;
  private _puntosConAoi = L.layerGroup();
  private _poligonos = L.layerGroup();
  private _fincas: FincaMapaResponse[] = [];
  private _capas = new Map<string, CapasFinca>();
  /** Fincas que muestra la lista del buscador, en orden. */
  private _resultados: FincaMapaResponse[] = [];
  /** Posición en `_resultados` de la que elige Enter (-1 = ninguna). */
  private _resaltado = -1;
  /** Cuántas fincas coinciden en total, aunque la lista muestre solo `MAX_RESULTADOS`. */
  private _totalEncontradas = 0;

  /**
   * @param _promotorId Promotor al que se acota la sesión (null = sin acotar). Si quita la finca de su
   *   último cliente, la finca sale de su cartera y del mapa; sin acotar queda sin cliente (0) para asignarla.
   */
  constructor(private readonly _promotorId: string | null = null) { super(); }

  /** Ruta de la página. */
  public getAppRoute(): string { return "/terceros/mapa-fincas"; }
  /** Título de la página. */
  public getPageTitle(): string | null { return "Mapa de fincas"; }

  /** Arma el encabezado con el buscador y el contenedor del mapa, y carga las fincas. */
  protected override renderPageContent(pageContent: HTMLDivElement): void {
    pageContent.className = "sx-page mf-page";
    pageContent.innerHTML = `
      <div class="mf-hd">
        <div>
          <div class="mf-hd-t">Mapa de fincas</div>
          <div class="mf-hd-s" id="mf-sub">Cargando…</div>
        </div>
        <div class="mf-bus">
          <i class="ph ph-magnifying-glass" aria-hidden="true"></i>
          <input class="mf-bus-in" id="mf-bus-in" type="search" placeholder="Buscar finca o cliente…" autocomplete="off"
            aria-label="Buscar finca o cliente" aria-controls="mf-bus-res" aria-expanded="false" disabled>
          <div class="mf-bus-res" id="mf-bus-res" role="listbox" hidden></div>
        </div>
      </div>
      <div class="mf-map" id="mf-map"></div>
    `;
    this._conectarBuscador();
    void this._cargar();
  }

  /** Libera el mapa de Leaflet. */
  protected override beforeDispose(): void {
    this._map?.remove();
    this._map = null;
  }

  /** Pide las fincas y las dibuja; sin conexión avisa y deja el mapa vacío. Al terminar habilita el buscador. */
  private async _cargar(): Promise<void> {
    const map = this._crearMapa();
    let fincas: FincaMapaResponse[] = [];
    try {
      fincas = await FincaMapaService.instance.getAll();
    } catch (error) {
      errorMessageComponent.addError("Error al cargar el mapa de fincas", (error as Error).message);
    }
    if (this._map !== map) return; // se salió de la página mientras cargaba

    this._fincas = fincas;
    this._pintarTotal();
    const buscador = document.getElementById("mf-bus-in") as HTMLInputElement | null;
    if (buscador) buscador.disabled = false;
    if (fincas.length === 0) return;

    const bounds = L.latLngBounds([]);
    for (const f of fincas) {
      const punto = L.marker([f.latitud, f.longitud], { icon: this._iconoPunto(f), keyboard: false })
        .bindPopup(() => this._popup(f));
      bounds.extend(punto.getLatLng());
      let poligono: L.GeoJSON | null = null;
      if (f.aoi) {
        this._puntosConAoi.addLayer(punto);
        poligono = L.geoJSON(f.aoi, { style: { color: COLOR, weight: 2.5, fillColor: COLOR, fillOpacity: 0.15 } })
          .bindPopup(() => this._popup(f))
          // De cerca el punto no se ve: la cantidad de clientes va en el centro del AOI.
          .bindTooltip(this._htmlCantidad(f), { permanent: true, direction: "center", className: "mf-aoi-n", interactive: false });
        this._poligonos.addLayer(poligono);
        bounds.extend(poligono.getBounds());
      } else {
        // Sin AOI solo hay punto: se ve a cualquier zoom.
        punto.addTo(map);
      }
      this._capas.set(f.id, { punto, poligono });
      this._actualizarCapas(f);
    }

    map.on("zoomend", () => this._aplicarZoom());
    this._aplicarZoom();
    map.fitBounds(bounds, { padding: [32, 32], maxZoom: 15 });
  }

  /** Mapa satelital con nombres (híbrido de Google), centrado en Colombia hasta que carguen las fincas. */
  private _crearMapa(): L.Map {
    const map = L.map(document.getElementById("mf-map")!, {
      center: [4.711, -74.0721],
      zoom: 6,
      maxZoom: 20,
      zoomControl: true,
      attributionControl: false,
    });
    L.tileLayer(
      `https://mt{s}.google.com/vt?lyrs=y&x={x}&y={y}&z={z}&key=${GOOGLE_MAPS_API_KEY}`,
      { maxZoom: 20, subdomains: ["0", "1", "2", "3"] },
    ).addTo(map);
    this._map = map;
    requestAnimationFrame(() => map.invalidateSize());
    return map;
  }

  /** Lejos: puntos. Cerca (desde `ZOOM_POLIGONOS`): los AOI en lugar de sus puntos. */
  private _aplicarZoom(): void {
    const map = this._map;
    if (!map) return;
    const cerca = map.getZoom() >= ZOOM_POLIGONOS;
    if (cerca) {
      map.removeLayer(this._puntosConAoi);
      this._poligonos.addTo(map);
    } else {
      map.removeLayer(this._poligonos);
      this._puntosConAoi.addTo(map);
    }
  }

  /** Subtítulo: cantidad de fincas del mapa y cuántas no tienen cliente. */
  private _pintarTotal(): void {
    const sub = document.getElementById("mf-sub");
    if (!sub) return;
    const total = this._fincas.length;
    const sinCliente = this._fincas.filter((f) => f.duenos.length === 0).length;
    sub.textContent = (total === 1 ? "1 finca" : `${total} fincas`) + (sinCliente > 0 ? ` · ${sinCliente} sin cliente` : "");
  }

  /**
   * Conecta el buscador del encabezado: busca mientras se escribe; con las flechas se mueve por la lista, Enter va a
   * la finca resaltada y Escape cierra la lista, que también se cierra al salir del campo. Se elige con `mousedown`
   * (y no con `click`) para que el campo no pierda el foco, y cierre la lista, antes de elegir.
   */
  private _conectarBuscador(): void {
    const input = document.getElementById("mf-bus-in") as HTMLInputElement | null;
    const lista = document.getElementById("mf-bus-res");
    if (!input || !lista) return;

    input.addEventListener("input", () => this._filtrar(input.value));
    input.addEventListener("focus", () => {
      if (input.value.trim() !== "") this._filtrar(input.value);
    });
    input.addEventListener("blur", () => this._cerrarResultados());
    input.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        this._cerrarResultados();
        return;
      }
      if (lista.hidden || this._resultados.length === 0) return;
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        const paso = e.key === "ArrowDown" ? 1 : -1;
        this._resaltado = Math.min(Math.max(this._resaltado + paso, 0), this._resultados.length - 1);
        this._pintarResultados();
      } else if (e.key === "Enter") {
        e.preventDefault();
        this._irAFinca(this._resultados[Math.max(this._resaltado, 0)]);
      }
    });
    lista.addEventListener("mousedown", (e) => {
      e.preventDefault();
      const item = (e.target as HTMLElement).closest<HTMLElement>("[data-i]");
      const f = item ? this._resultados[Number(item.dataset.i)] : undefined;
      if (f) this._irAFinca(f);
    });
  }

  /** Busca con el texto escrito y pinta la lista con la primera finca resaltada; con el campo vacío la cierra. */
  private _filtrar(texto: string): void {
    if (texto.trim() === "") {
      this._cerrarResultados();
      return;
    }
    const encontradas = this._buscar(texto);
    this._totalEncontradas = encontradas.length;
    this._resultados = encontradas.slice(0, MAX_RESULTADOS);
    this._resaltado = this._resultados.length > 0 ? 0 : -1;
    this._pintarResultados();
  }

  /**
   * Fincas que tienen todas las palabras del texto (regla común de `busquedaTexto`: sin distinguir mayúsculas ni
   * tildes) en los nombres que les dan sus clientes, en los nombres de esos clientes o, si no tienen cliente, en su
   * último nombre. Ordenadas por el nombre que muestra la lista. Lee `_fincas` en cada búsqueda, así que ya ve los
   * dueños asignados o quitados.
   */
  private _buscar(texto: string): FincaMapaResponse[] {
    const palabras = palabrasBusqueda(texto);
    if (palabras.length === 0) return [];
    return this._fincas
      .filter((f) => coincideBusqueda(palabras, ...f.duenos.flatMap((d) => [d.nombreFinca, d.clienteNombre]), f.nombreUltimo))
      .sort((a, b) => this._tituloResultado(a).localeCompare(this._tituloResultado(b)));
  }

  /** Nombre de la finca en la lista: los que le dan sus clientes, sin repetir, o sin cliente su último nombre. */
  private _tituloResultado(f: FincaMapaResponse): string {
    if (f.duenos.length === 0) return f.nombreUltimo ?? "Finca sin nombre";
    return [...new Set(f.duenos.map((d) => d.nombreFinca))].join(" / ");
  }

  /**
   * Pinta la lista del buscador: cada finca con sus clientes ("Sin cliente" si no tiene), la resaltada marcada y, si
   * hay más de `MAX_RESULTADOS`, cuántas faltan.
   */
  private _pintarResultados(): void {
    const input = document.getElementById("mf-bus-in");
    const lista = document.getElementById("mf-bus-res");
    if (!input || !lista) return;
    if (this._resultados.length === 0) {
      lista.innerHTML = `<div class="mf-bus-vacio">Sin resultados</div>`;
    } else {
      const faltan = this._totalEncontradas - this._resultados.length;
      lista.innerHTML = this._resultados.map((f, i) => `
        <div class="mf-bus-it${i === this._resaltado ? " mf-bus-it-on" : ""}" role="option" data-i="${i}" aria-selected="${i === this._resaltado}">
          <div class="mf-bus-n">${esc(this._tituloResultado(f))}</div>
          <div class="mf-bus-c">${f.duenos.length === 0 ? "Sin cliente" : esc(f.duenos.map((d) => d.clienteNombre).join(", "))}</div>
        </div>`).join("")
        + (faltan > 0 ? `<div class="mf-bus-mas">${faltan === 1 ? "1 más" : `${faltan} más`}: sigue escribiendo para acotar</div>` : "");
      lista.querySelector(".mf-bus-it-on")?.scrollIntoView({ block: "nearest" });
    }
    lista.hidden = false;
    input.setAttribute("aria-expanded", "true");
  }

  /** Cierra la lista del buscador; el texto escrito queda en el campo. */
  private _cerrarResultados(): void {
    const lista = document.getElementById("mf-bus-res");
    if (lista) lista.hidden = true;
    document.getElementById("mf-bus-in")?.setAttribute("aria-expanded", "false");
    this._resultados = [];
    this._resaltado = -1;
  }

  /**
   * Lleva el mapa a la finca elegida en el buscador y abre su popup. Con AOI, al zoom que la muestra entera (hasta
   * `ZOOM_MAX_FINCA`), pero nunca por debajo de `ZOOM_POLIGONOS`, para que se dibuje el polígono y no el punto; sin
   * AOI, a su punto. Va sin animación: así el cambio de capas de `_aplicarZoom` ya ocurrió cuando se abre el popup.
   */
  private _irAFinca(f: FincaMapaResponse): void {
    this._cerrarResultados();
    const map = this._map;
    const capas = this._capas.get(f.id);
    if (!map || !capas) return;
    if (capas.poligono) {
      const limites = capas.poligono.getBounds();
      const zoom = Math.max(Math.min(map.getBoundsZoom(limites, false, L.point(64, 64)), ZOOM_MAX_FINCA), ZOOM_POLIGONOS);
      map.setView(limites.getCenter(), zoom, { animate: false });
      capas.poligono.openPopup(limites.getCenter());
    } else {
      map.setView(capas.punto.getLatLng(), Math.max(map.getZoom(), ZOOM_PUNTO), { animate: false });
      capas.punto.openPopup();
    }
  }

  /** Repinta la cantidad de clientes en el punto y en el centro del AOI (el color es el mismo para todas). */
  private _actualizarCapas(f: FincaMapaResponse): void {
    const capas = this._capas.get(f.id);
    if (!capas) return;
    capas.punto.setIcon(this._iconoPunto(f));
    capas.poligono?.setTooltipContent(this._htmlCantidad(f));
  }

  /** Círculo con la cantidad de clientes de la finca (0 = sin cliente). */
  private _htmlCantidad(f: FincaMapaResponse): string {
    const n = f.duenos.length;
    const titulo = n === 0 ? "Sin cliente" : n === 1 ? "1 cliente" : `${n} clientes`;
    return `<div class="mf-pin" title="${titulo}">${n}</div>`;
  }

  /** Ícono del punto: el círculo con la cantidad, centrado en la ubicación. */
  private _iconoPunto(f: FincaMapaResponse): L.DivIcon {
    return L.divIcon({ className: "mf-pin-wrap", html: this._htmlCantidad(f), iconSize: [24, 24], iconAnchor: [12, 12], popupAnchor: [0, -10] });
  }

  /** Popup de la finca (Leaflet lo arma cada vez que se abre, así que siempre refleja los dueños actuales). */
  private _popup(f: FincaMapaResponse): HTMLElement {
    const el = document.createElement("div");
    el.className = "mf-pop";
    this._pintarPopup(el, f);
    return el;
  }

  /**
   * Encabezado según los clientes: sin cliente, "Sin cliente" (con el último nombre que tuvo la finca); con
   * uno, el nombre de la finca; con varios, "N clientes". En la lista, cada cliente con el nombre que le da a
   * la finca y su "quitar". Al final, asignar a un cliente (otro, si ya tiene) y, solo en las fincas sin
   * cliente, "Eliminar finca" (2026-10-16; un promotor nunca ve fincas sin cliente).
   */
  private _pintarPopup(el: HTMLElement, f: FincaMapaResponse): void {
    const n = f.duenos.length;
    const puedeEliminar = n === 0 && !this._promotorId;
    const titulo = n === 0 ? "Sin cliente" : n === 1 ? f.duenos[0].nombreFinca : `${n} clientes`;
    const sub = n === 0
      ? (f.nombreUltimo ? `Último nombre: ${esc(f.nombreUltimo)}` : "")
      : n === 1 ? "Cliente" : "";
    el.innerHTML = `
      <div class="mf-pop-t${n === 0 ? " mf-pop-t-sin" : ""}">${esc(titulo)}</div>
      ${sub ? `<div class="mf-pop-l">${sub}</div>` : ""}
      ${n > 0 ? `
      <div class="mf-pop-dn">
        ${f.duenos.map((d) => `
          <div class="mf-pop-row">
            <div class="mf-pop-info">
              <span class="mf-pop-cli">${esc(d.clienteNombre)}</span>
              ${n > 1 ? `<span class="mf-pop-fn">${esc(d.nombreFinca)}</span>` : ""}
            </div>
            <button class="mf-pop-x" type="button" data-quitar="${esc(d.clienteId)}" title="Quitar la finca de este cliente">
              <i class="ph ph-x"></i>
            </button>
          </div>`).join("")}
      </div>` : ""}
      <button class="mf-pop-add" type="button"><i class="ph ph-user-plus"></i> ${n > 0 ? "Asignar a otro cliente" : "Asignar a un cliente"}</button>
      ${puedeEliminar ? `
      <button class="sx-btn sx-btn-danger sx-btn-sm" type="button" data-eliminar style="width:100%;margin-top:6px">
        <i class="ph ph-trash"></i> Eliminar finca
      </button>` : ""}
    `;
    el.querySelector<HTMLButtonElement>("button[data-eliminar]")?.addEventListener("click", () => this._confirmarEliminar(f));
    el.querySelectorAll<HTMLButtonElement>("button[data-quitar]").forEach((btn) => {
      const dueno = f.duenos.find((d) => d.clienteId === btn.dataset.quitar);
      if (dueno) btn.onclick = () => this._confirmarQuitar(el, f, dueno);
    });
    el.querySelector<HTMLButtonElement>(".mf-pop-add")!.onclick = () => {
      new FincaAsignarClienteModal(f, (nuevo) => {
        const eraSinCliente = f.duenos.length === 0;
        f.duenos = [...f.duenos, nuevo].sort((a, b) => a.clienteNombre.localeCompare(b.clienteNombre));
        f.nombreUltimo = null;
        this._pintarPopup(el, f);
        this._actualizarCapas(f);
        this._pintarTotal();
        errorMessageComponent.addSuccess("Finca asignada", eraSinCliente
          ? `${nuevo.nombreFinca} ahora es de ${nuevo.clienteNombre}.`
          : `${nuevo.nombreFinca} ahora también es de ${nuevo.clienteNombre}.`);
      }).init();
    };
  }

  /** Pide confirmación para quitar la finca del cliente; si es su único cliente avisa cómo queda. */
  private _confirmarQuitar(el: HTMLElement, f: FincaMapaResponse, dueno: DuenoFincaMapa): void {
    const ultimo = f.duenos.length === 1;
    const queda = !ultimo
      ? "La finca sigue con sus otros clientes."
      : this._promotorId
        ? "Es su único cliente: la finca queda sin cliente y sale de tu cartera."
        : "Es su único cliente: la finca queda sin cliente (0 en el mapa), para asignarla a otro.";
    new ConfirmDialogComponent({
      title: "Quitar finca del cliente",
      message: `¿Quitar la finca ${esc(dueno.nombreFinca)} de ${esc(dueno.clienteNombre)}? ${queda}`,
      confirmLabel: "Quitar",
      onConfirm: () => void this._quitar(el, f, dueno),
    }).init();
  }

  /**
   * Da de baja la relación cliente-finca (`EliminarRelacion`; la finca no se borra). Si era su último cliente,
   * la finca queda sin cliente (0) para asignarla; a un promotor se le va de la cartera y sale del mapa, como la
   * dejaría el Server en la próxima carga.
   */
  private async _quitar(el: HTMLElement, f: FincaMapaResponse, dueno: DuenoFincaMapa): Promise<void> {
    try {
      await FincaDeleteService.instance.delete(f.id, dueno.clienteId);
    } catch (error) {
      errorMessageComponent.addError("No se pudo quitar la finca", (error as Error).message);
      return;
    }
    errorMessageComponent.addSuccess("Finca quitada", `${dueno.nombreFinca} ya no está asociada a ${dueno.clienteNombre}.`);
    f.duenos = f.duenos.filter((d) => d.clienteId !== dueno.clienteId);
    if (f.duenos.length === 0) f.nombreUltimo = dueno.nombreFinca;
    if (f.duenos.length > 0 || !this._promotorId) {
      this._pintarPopup(el, f);
      this._actualizarCapas(f);
      this._pintarTotal();
      return;
    }
    this._sacarDelMapa(f);
  }

  /**
   * Pide confirmación para eliminar una finca sin cliente. El servidor revisa otra vez que no tenga clientes ni
   * datos y, si los tiene, responde con el motivo.
   */
  private _confirmarEliminar(f: FincaMapaResponse): void {
    const nombre = f.nombreUltimo ? `la finca ${esc(f.nombreUltimo)}` : "esta finca";
    new ConfirmDialogComponent({
      title: "Eliminar finca",
      message: `¿Eliminar ${nombre}? Sale del mapa y su área queda libre para registrar otra finca. `
        + "No se puede deshacer desde Plataforma.",
      confirmLabel: "Eliminar",
      onConfirm: () => void this._eliminar(f),
    }).init();
  }

  /** Da de baja la finca (`EliminarFinca`) y la saca del mapa. Si el servidor la rechaza, muestra el motivo. */
  private async _eliminar(f: FincaMapaResponse): Promise<void> {
    try {
      await FincaEliminarService.instance.eliminar(f.id);
    } catch (error) {
      errorMessageComponent.addError("No se pudo eliminar la finca", (error as Error).message);
      return;
    }
    errorMessageComponent.addSuccess("Finca eliminada", f.nombreUltimo
      ? `${f.nombreUltimo} se eliminó y su área quedó libre.`
      : "La finca se eliminó y su área quedó libre.");
    this._sacarDelMapa(f);
  }

  /** Cierra el popup y quita la finca del mapa y del total, sin volver a pedir el listado. */
  private _sacarDelMapa(f: FincaMapaResponse): void {
    this._map?.closePopup();
    const capas = this._capas.get(f.id);
    for (const capa of capas ? [capas.punto, capas.poligono] : []) {
      if (!capa) continue;
      this._puntosConAoi.removeLayer(capa);
      this._poligonos.removeLayer(capa);
      capa.remove();
    }
    this._capas.delete(f.id);
    this._fincas = this._fincas.filter((x) => x.id !== f.id);
    this._pintarTotal();
  }
}

/** Escapa texto para insertarlo en HTML. */
function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);
}
